import React, { useState, useEffect } from 'react';
import { ExternalLink, TrendingUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Default investments (used as fallback)
const defaultInvestments = [
  {
    id: '1',
    name: "Havok Truck Accessories, LLC",
    website: "www.havoktrucks.com",
    website_url: "http://www.havoktrucks.com/",
    description: "Shire Advisors, LLC has a minority investment in this truck accessory and performance business. Shire has also provided a five-year working capital loan to grow and develop the business.",
    additional_info: "Need your vehicle accessorized? Call (940) 320-8818 or check out the e-commerce site.",
    logo: "https://static.wixstatic.com/media/268b46_8db19414b7674a4c911c8a4c2a2847a0~mv2.png/v1/fill/w_154,h_137,al_c,lg_1,q_85,enc_avif,quality_auto/HavokTinyLogo.png",
    category: "Automotive",
    order: 0,
    visible: true
  },
  {
    id: '2',
    name: "E-Muscle Car, LLC",
    website: "www.e-musclecars.com",
    website_url: "http://www.e-musclecars.com/",
    description: "E-Muscle Car is a unique concept to take classic 1965-1969 American muscle cars like Camaros, Mustangs or Corvettes and convert them from internal combustion engines to electric motor vehicles. Merging the beauty and style of these older cars with today's modern technology and silent electric propulsion.",
    additional_info: "Currently in R&D prototyping with commercially available vehicles expected soon.",
    logo: "https://static.wixstatic.com/media/268b46_9326de3476e44dcda29324e01fc339ec~mv2.jpg/v1/fill/w_220,h_70,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/E-Muscle-Logo.jpg",
    category: "Automotive/Electric",
    order: 1,
    visible: true
  },
  {
    id: '3',
    name: "Astro Power Systems, Inc.",
    website: "www.AstroPowerSystems.com",
    website_url: "http://www.astropowersystems.com/",
    description: "Astro Power Systems, Inc. was incorporated in June 1993 in Texas. Originally an electrical service and distribution company, Astro divested from that business in 2020.",
    additional_info: "Astro is now focused on the solar power arena and sustainable localized power conversions.",
    logo: "https://static.wixstatic.com/media/268b46_d0ca5527cd7840f58d33ad0b907c62fa~mv2.png/v1/fill/w_158,h_162,al_c,lg_1,q_85,enc_avif,quality_auto/AstroPower-Symbol.png",
    category: "Energy/Solar",
    order: 2,
    visible: true
  },
  {
    id: '4',
    name: "Bolivar - A Film By Nell Teare",
    website: null,
    website_url: null,
    description: "Shire Advisors, LLC through one of its principals is an Executive Producer of a small independent movie named Bolivar. Directed by Nell Teare, it stars Robert Pine, Tracie Thoms and Alex Desert.",
    additional_info: "The film has been screened at summer festivals and other venues.",
    logo: "https://static.wixstatic.com/media/268b46_230989c6f3d74fdbb32a6b486a8eb4c6~mv2.png/v1/fill/w_220,h_190,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Boliver_Title.png",
    category: "Entertainment/Film",
    order: 3,
    visible: true
  },
  {
    id: '5',
    name: "Honey Farm Estates",
    website: null,
    website_url: null,
    description: "Honey Farm Estates is a 9 acre residential development off Ft. Worth Drive in north Denton. With a beautiful water feature, these spectacular one acre lots offer premium living.",
    additional_info: "Big Lots in Denton - contact us for more information.",
    logo: "https://static.wixstatic.com/media/1ae1fa3c94da44ed90f789433558d966.png/v1/fill/w_220,h_220,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Client%201.png",
    category: "Real Estate",
    order: 4,
    visible: true
  }
];

const Investments = () => {
  const [investments, setInvestments] = useState(defaultInvestments);
  const [header, setHeader] = useState({
    label: "Portfolio",
    title: "Equity Investments & Clients",
    description: "Just a few of the businesses we believe in"
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [investmentsRes, headerRes] = await Promise.all([
          axios.get(`${API}/content/investments`),
          axios.get(`${API}/content/section-headers/investments`)
        ]);
        if (investmentsRes.data && investmentsRes.data.length > 0) {
          setInvestments(investmentsRes.data.filter(item => item.visible !== false));
        }
        if (headerRes.data) {
          setHeader(headerRes.data);
        }
      } catch (error) {
        console.log('Using default investments');
      }
    };
    fetchData();
  }, []);

  return (
    <section id="investments" className="py-8 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-8">
          <span className="inline-block text-gold-600 font-semibold text-xs tracking-[0.2em] uppercase mb-2">
            {header.label}
          </span>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-navy-900 mb-3">
            {header.title}
          </h2>
          <p className="text-base text-gray-600 leading-relaxed">
            {header.description}
          </p>
        </div>

        {/* Investments Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {investments
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((investment) => (
            <div
              key={investment.id}
              className="panel-flair group bg-white rounded-xl overflow-hidden border-2 border-slate-200/70 hover:border-gold-400 transition-all duration-300 hover:-translate-y-1 flex flex-col h-full"
            >
              <div className="p-4 flex flex-col flex-1">
                {/* Header row: logo thumbnail on the left, name + category on the right */}
                <div className="flex items-start gap-3 mb-2">
                  <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {investment.logo ? (
                      <img
                        src={investment.logo}
                        alt={`${investment.name} logo`}
                        loading="lazy"
                        decoding="async"
                        className="max-h-10 max-w-10 object-contain group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <span className="text-lg font-bold text-navy-900">
                        {investment.name?.charAt(0) || 'I'}
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-navy-900 leading-tight group-hover:text-gold-600 transition-colors">
                      {investment.name}
                    </h3>
                    {investment.category && (
                      <span className="inline-block mt-0.5 px-2 py-0.5 bg-navy-900/10 text-navy-800 rounded-full text-[10px] font-medium">
                        {investment.category}
                      </span>
                    )}
                  </div>
                </div>

                <p className="text-gray-600 text-sm leading-snug mb-2">
                  {investment.description}
                </p>

                {investment.additional_info && (
                  <p className="text-gray-500 text-xs italic mb-2 leading-snug">
                    {investment.additional_info}
                  </p>
                )}

                {investment.website_url && (
                  <a
                    href={investment.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-auto pt-2 inline-flex items-center text-gold-600 font-semibold text-sm hover:text-gold-700 transition-colors"
                  >
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                    {investment.website || investment.website_url}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Note */}
        <div className="mt-10 text-center">
          <div className="inline-flex items-center gap-3 px-5 py-3 bg-navy-900/5 rounded-xl">
            <TrendingUp className="w-4 h-4 text-gold-600" />
            <p className="text-gray-600 text-sm">
              Our typical NDA does not allow us to publicize the names of our clients. For references and a Client List please{' '}
              <a href="#contact" className="text-gold-600 font-semibold hover:underline">
                contact us
              </a>.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Investments;
